import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from '@emotion/styled'
import type { Campaign, CampaignV2 } from '@mobi/betslip/types'
import { setInvestmentAmount } from '@mobi/betslip/Store/Bets'
import {
  selectBetSlipItemBonusBet,
  selectBetSlipItemIsEachWayAvailable,
} from '@mobi/betslip/Store/Bets/selectors'
import { RadioButton } from '@mobi/component-library/Selection&Input/RadioButton'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { ToggleWithLabels } from '@mobi/component-library/Selection&Input/ToggleWithLabels'
import type { PromoType, PromoTypeClass } from '../../types'
import { promoTypeColorThemes } from '../../constants'
import { getHostHelpers } from '@mobi/betslip/helpers/hostHelpers'
import { FeatureFlags } from '@mobi/settings/featureFlags'

export const PromoSelection: React.FC<{
  itemId: string
  promoType: PromoType
  title: string
  isSelected: boolean
  hasActiveSelection: boolean
  onClick: (() => void) | ((bonusBetInvestmentType: 'Win' | 'Place') => void)
  setSelectionHeight: React.Dispatch<React.SetStateAction<number>>
  isPromoSelectorExpanded: boolean
  renderPosition: number
  descriptionSlots?: React.ReactNode[]
  passedCampaign?: Campaign | undefined
  betsRemaining?: number
  openSplitBonusBetTray?: (campaign: CampaignV2) => void
}> = ({
  itemId,
  promoType,
  title,
  isSelected,
  hasActiveSelection,
  onClick,
  setSelectionHeight,
  isPromoSelectorExpanded,
  renderPosition,
  descriptionSlots,
  passedCampaign,
  betsRemaining,
  openSplitBonusBetTray,
}) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()

  const isEachWayAvailable = useSelector(selectBetSlipItemIsEachWayAvailable(itemId))

  const [bonusBetInvestmentType, setBonusBetInvestmentType] = React.useState<'Win' | 'Place'>('Win')

  React.useEffect(() => {
    if (!ref.current) return

    const selectionHeight = ref.current.getBoundingClientRect().height
    if (isSelected) {
      setSelectionHeight(selectionHeight)
    }

    if (!isPromoSelectorExpanded) {
      ref.current.style.transition = hasActiveSelection
        ? 'transform 0.4s ease, opacity 0.2s ease'
        : ''
      ref.current.style.transform = `translateY(${isSelected ? `-${selectionHeight * renderPosition}px` : '0px'})`
      ref.current.style.opacity = !hasActiveSelection || isSelected ? '1' : '0'
      ref.current.style.borderColor = isSelected ? colors.white : ''
    } else {
      ref.current.style.transform = 'translateY(0px)'
      ref.current.style.transition = hasActiveSelection
        ? 'transform 0.4s ease, opacity 0.3s ease 0.2s'
        : ''
      ref.current.style.opacity = '1'
      ref.current.style.borderColor = ''
    }
  }, [isSelected, setSelectionHeight, isPromoSelectorExpanded, renderPosition, hasActiveSelection])

  const promoTypeClass: PromoTypeClass = `PromoType-${promoType}`

  const selectedCampaignInvestmentType = useSelector(selectBetSlipItemBonusBet(itemId))

  const useFeature = getHostHelpers().useFeature
  const isSplitBonusFeatureActive = useFeature(FeatureFlags.SPLIT_BONUS_BETS.key)
  const isCampaignsV2FeatureActive = useFeature(FeatureFlags.GET_CAMPAIGNS_V2.key)
  const isBetSplittingAndCampaignsV2Active = isSplitBonusFeatureActive && isCampaignsV2FeatureActive

  const isCampaignV2 = getHostHelpers().isCampaignV2
  const isCampaignV2Bonus = passedCampaign && isCampaignV2(passedCampaign)

  const canBeSplit =
    isCampaignV2Bonus && isBetSplittingAndCampaignsV2Active
      ? passedCampaign?.tokenCanBeSplit
      : false

  const isBonusBet = passedCampaign?.rewardType === 'BonusBetReward'

  const trackBonusOfferClick = getHostHelpers().trackBonusOfferClick

  const handleBonusBetSplitClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    if (isCampaignV2Bonus && !!openSplitBonusBetTray) {
      openSplitBonusBetTray(passedCampaign)
      trackBonusOfferClick(passedCampaign, 'Split Bonus Bet')
    }
  }

  return (
    <WrapperStyled
      className={promoTypeClass}
      ref={ref}
      onClick={
        promoType === 'Bonus'
          ? () =>
              (onClick as (bonusBetInvestmentType: 'Win' | 'Place') => void)(bonusBetInvestmentType)
          : (onClick as () => void)
      }
    >
      <div>
        <span>
          {promoType === 'Bonus' && (
            <Icon name='LineCurrencyDollarCircle' color={colors.celery[600]} size='1.6rem' />
          )}

          {promoType === 'KambiReward' && (
            <Icon name='LineGift01' color={colors.studio[600]} size='1.75rem' />
          )}
        </span>
      </div>

      <div>
        <h4>{title}</h4>

        {isBonusBet && canBeSplit ? (
          <>
            <span>{descriptionSlots && descriptionSlots[1]}</span>
            <button type='button' onClick={e => handleBonusBetSplitClick(e)}>
              Split Bonus Bet
            </button>
          </>
        ) : isBonusBet &&
          isBetSplittingAndCampaignsV2Active &&
          betsRemaining &&
          betsRemaining > 1 ? (
          <>
            <span>{descriptionSlots && descriptionSlots[1]}</span>
            <span className='bets-remaining'>{betsRemaining} Remaining</span>
          </>
        ) : (
          descriptionSlots?.map((slot, slotNum) => slot && <span key={slotNum}>{slot}</span>)
        )}
      </div>

      {promoType === 'Bonus' && isBonusBet && itemId !== 'Multi' && !isEachWayAvailable && (
        <div onClick={e => e.stopPropagation()}>
          <ToggleWithLabels
            id={itemId + passedCampaign.id}
            onToggle={() => {
              if (isSelected) {
                dispatch(
                  setInvestmentAmount({
                    itemId,
                    investmentType: bonusBetInvestmentType === 'Win' ? 'Place' : 'Win',
                    value: `${passedCampaign?.remainingAmount}`,
                  })
                )
                dispatch(
                  setInvestmentAmount({
                    itemId,
                    investmentType: bonusBetInvestmentType === 'Win' ? 'Win' : 'Place',
                    value: `${0}`,
                  })
                )
              }

              setBonusBetInvestmentType(bonusBetInvestmentType === 'Win' ? 'Place' : 'Win')
            }}
            leftValue='Win'
            rightValue='Place'
            isLeftValueActive={
              isSelected
                ? selectedCampaignInvestmentType === 'Win'
                : bonusBetInvestmentType === 'Win'
            }
          />
        </div>
      )}

      <div>
        <RadioButton
          id={itemId + title + passedCampaign?.id}
          isChecked={isSelected}
          type='Tick'
          size='md'
          onClick={
            promoType === 'Bonus'
              ? () =>
                  (onClick as (bonusBetInvestmentType: string, campaignId: number) => void)(
                    bonusBetInvestmentType,
                    passedCampaign?.id ?? 0
                  )
              : (onClick as () => void)
          }
        />
      </div>
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div(
  {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    borderBottom: '0.1rem solid var(--light-color)',
    fontFamily: font.family.primary,
    backgroundColor: colors.white,

    transition: 'transform 0.4s ease',
    transform: 'translateY(0px)',
    opacity: 1,

    ':last-of-type': { border: 0 },

    // Icon
    '> div:nth-of-type(1)': {
      '> span': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '3.2rem',
        height: '3.2rem',
        borderRadius: radius.full,
        backgroundColor: 'var(--light-color)',
      },
    },

    // Title
    '> div:nth-of-type(2)': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: spacing.smx2,

      '> h4': {
        margin: 0,
        padding: 0,
        color: colors.black,
        fontSize: font.size.md.fontSize,
        letterSpacing: font.size.md.letterSpacing,
        lineHeight: font.size.md.lineHeight,
        fontWeight: font.weight.medium,
      },

      '> span, > button': {
        fontSize: font.size.sm.fontSize,
        letterSpacing: font.size.sm.letterSpacing,
        lineHeight: font.size.sm.lineHeight,
        fontWeight: font.weight.regular,
        color: colors.neutral[800],
      },

      '> span.bets-remaining': {
        backgroundColor: colors.neutral[200],
        color: colors.neutral[900],
        fontSize: font.size.base.fontSize,
        borderRadius: radius.sm,
        padding: '0.2rem 0.4rem',
      },

      '> button': {
        fontFamily: font.family.primary,
        background: 'none',
        border: 'none',
        padding: 0,
        textDecoration: 'underline',
        textUnderlineOffset: '3px',
        cursor: 'pointer',
      },
    },

    // Radio Button
    '> div:nth-of-type(3)': {
      marginLeft: 'auto',
    },
  },
  promoTypeColorThemes
)
